import { useState } from 'react';
import * as Rx from '@radix-ui/react-dropdown-menu';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, HelpCircle, LogOut, Mail, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shared/ui/DropdownMenu';
import { useAuth } from '../auth/useAuth';
import { useOnboarding } from '../features/onboarding/components/OnboardingProvider';
import { useSetActiveSubscription, useSubscriptions } from '../features/subscriptions/api';
import { SubscriptionActionIcons } from '../features/subscriptions/SubscriptionActionIcons';
import { clipLabel, resolveSubscriptionLabel } from '../features/subscriptions/labels';

/**
 * Объединённое меню профиля: иконка + переключатель подписок + «Выйти».
 * У каждой подписки две иконки: изменить название и открыть карточку.
 */
export function ProfileMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, logout } = useAuth();
  const subsQuery = useSubscriptions(true);
  const setActive = useSetActiveSubscription();
  const onboarding = useOnboarding();
  const [open, setOpen] = useState(false);

  const items = subsQuery.data?.items ?? [];
  const activeId = subsQuery.data?.activeSubscriptionId ?? null;
  const displayName = profile?.name ?? profile?.email ?? t('nav.account');
  const hasSubscriptions = items.length >= 1;

  const labelOf = (label: string | null, index: number): string =>
    resolveSubscriptionLabel(label, index, (n) => t('subscriptions.defaultName', { n }));

  const pick = (id: string): void => {
    if (id !== activeId) setActive.mutate(id);
    setOpen(false);
  };

  const openCard = (id: string, rename = false): void => {
    setOpen(false);
    void navigate({
      to: '/subscriptions/$subscriptionId',
      params: { subscriptionId: id },
      search: rename ? { rename: true } : {},
    });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full bg-page px-2 py-1.5 text-sm text-fg sm:px-3"
          title={displayName}
        >
          <User className="h-4 w-4 shrink-0" />
          <span className="hidden max-w-[140px] truncate sm:inline">{displayName}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-fg-muted" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-72 max-w-[calc(100vw-1.5rem)]">
        {profile?.email && (
          <div className="truncate px-3 py-2 text-xs text-fg-muted" title={profile.email}>
            {profile.email}
          </div>
        )}

        {hasSubscriptions && (
          <>
            <Rx.Separator className="my-1 h-px bg-border" />
            <Rx.Label className="px-3 py-1 text-xs font-medium text-fg-muted">
              {items.length > 1 ? t('subscriptions.switch') : t('subscriptions.one')}
            </Rx.Label>
            {items.map((s, index) => {
              const full = labelOf(s.serviceLabel, index);
              return (
                <div
                  key={s.serviceSubscriptionId}
                  className="flex items-center gap-1 rounded-lg px-1.5 py-0.5 data-[highlighted]:bg-page"
                >
                  <button
                    type="button"
                    disabled={setActive.isPending}
                    onClick={() => {
                      pick(s.serviceSubscriptionId);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1.5 text-left text-sm text-fg hover:bg-page"
                  >
                    <Mail className="h-4 w-4 shrink-0 text-brand" />
                    <span className="min-w-0 flex-1 truncate" title={full}>
                      {clipLabel(full)}
                    </span>
                    {s.serviceSubscriptionId === activeId && (
                      <Check className="h-4 w-4 shrink-0 text-brand" />
                    )}
                  </button>
                  <SubscriptionActionIcons
                    onRename={() => {
                      openCard(s.serviceSubscriptionId, true);
                    }}
                    onOpenCard={() => {
                      openCard(s.serviceSubscriptionId);
                    }}
                  />
                </div>
              );
            })}
          </>
        )}

        {onboarding.isAvailable && (
          <>
            <Rx.Separator className="my-1 h-px bg-border" />
            <DropdownMenuItem
              icon={<HelpCircle />}
              onSelect={() => {
                onboarding.replay();
              }}
            >
              {t('onboarding.common.replay')}
            </DropdownMenuItem>
          </>
        )}

        <Rx.Separator className="my-1 h-px bg-border" />
        <DropdownMenuItem
          destructive
          icon={<LogOut />}
          onSelect={() => {
            logout();
          }}
        >
          {t('auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
